import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { type Result } from '@carvana/merch-result';
import deepmerge from 'deepmerge';
import { isPlainObject } from 'is-plain-object';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type {
  AvailabilityInfo,
  Pagination,
  UserDeliveryInfo,
  Vehicle
} from '~/contracts/srp';
import useShouldCalculateEvTagWidth from '~/hooks/srp/useShouldCalculateEvTagWidth';
import type { PricingResponse } from '~/services/srp/contracts';
import type { DeepPartial } from '~/types';
import isServerSideRendering from '~/utilities/isServerSideRendering';

function getPageNumberFromUrl(): number {
  if (isServerSideRendering) return 1;

  const pageNumber = parseInt(
    new URL(window.location.href).searchParams.get('page') ?? '1',
    10
  );

  return Number.isNaN(pageNumber) || pageNumber <= 1 ? 1 : pageNumber;
}

export type Inventory = {
  inventory: {
    pagination: Partial<Pagination>;
    vehicles: Vehicle[];
  };
  searchRequestId: string;
  userDeliveryInfo?: UserDeliveryInfo;
};

export type SimilarVehicles = { vehicles: Vehicle[]; searchRequestId: string };

export type AvailabilityData = {
  soonestPickup: Result<AvailabilityInfo>;
  soonestDelivery: Result<AvailabilityInfo>;
};

type ContextType = {
  inventory: LoadableState<Inventory>;
  inventoryBulkData: LoadableState<Record<number, AvailabilityData>>;
  previousSearchRequestId: string;
  resetInventoryPricing: () => void;
  setPreviousSearchRequestId: React.Dispatch<React.SetStateAction<string>>;
  setSimilarVehicles: React.Dispatch<React.SetStateAction<SimilarVehicles>>;
  shouldCalculateEvWidthInventory: boolean;
  shouldCalculateEvWidthSimilar: boolean;
  similarVehicles: SimilarVehicles;
  updateInventoryPricing: (_data: PricingResponse) => void;
};

export const defaultInventory: Inventory = {
  inventory: {
    pagination: {
      currentPage: getPageNumberFromUrl()
    },
    vehicles: []
  },
  searchRequestId: '',
  userDeliveryInfo: undefined
};

const defaultContext: ContextType = {
  inventory: defaultLoadableState<Inventory>(defaultInventory),
  inventoryBulkData: defaultLoadableState<Record<number, AvailabilityData>>({}),
  previousSearchRequestId: '',
  resetInventoryPricing() {},
  setPreviousSearchRequestId() {},
  setSimilarVehicles() {},
  shouldCalculateEvWidthInventory: false,
  shouldCalculateEvWidthSimilar: false,
  similarVehicles: {
    searchRequestId: '',
    vehicles: []
  },
  updateInventoryPricing() {}
};

const InventoryContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  inventoryData?: Inventory;
  similarVehiclesFromServer?: SimilarVehicles;
};

export function InventoryContextProvider({
  children,
  inventoryData,
  similarVehiclesFromServer
}: React.PropsWithChildren<OwnProps>) {
  const loadableInventoryState = useLoadableState<Inventory>(
    inventoryData ?? defaultInventory,
    {
      initializeAsLoaded: Boolean(
        inventoryData?.inventory?.vehicles?.length ||
          similarVehiclesFromServer?.vehicles?.length
      )
    }
  );
  const [similarVehicles, setSimilarVehicles] = useState<SimilarVehicles>(
    similarVehiclesFromServer ?? defaultContext.similarVehicles
  );
  const inventoryBulkData = useLoadableState<Record<number, AvailabilityData>>(
    {}
  );

  const [previousSearchRequestId, setPreviousSearchRequestId] = useState('');

  const { shouldCalculateEvWidthInventory, shouldCalculateEvWidthSimilar } =
    useShouldCalculateEvTagWidth(
      loadableInventoryState.data.inventory.vehicles,
      similarVehicles.vehicles
    );

  const updateInventoryPricing = useCallback(
    (payload: PricingResponse) => {
      loadableInventoryState.setData.call(
        null,
        prevState => ({
          ...prevState,
          inventory: {
            ...prevState.inventory,
            vehicles: prevState.inventory.vehicles.map(vehicle => {
              const {
                pricingErrorMessage = '',
                termInMonths,
                ...pricingData
              } = payload.vehiclePaymentTermsMapping[vehicle.vehicleId] ?? {
                pricingErrorMessage: 'Terms not found from pricing'
              };

              return {
                ...vehicle,
                price: {
                  ...vehicle.price,
                  ...pricingData,
                  pricingErrorMessage,
                  term: termInMonths
                }
              };
            })
          }
        }),
        { shouldSetLoaded: false }
      );
    },
    [loadableInventoryState.setData]
  );

  const resetInventoryPricing = useCallback(() => {
    loadableInventoryState.setData.call(
      null,
      prevState => ({
        ...prevState,
        inventory: {
          ...prevState.inventory,
          vehicles: prevState.inventory.vehicles.map(vehicle => {
            return {
              ...vehicle,
              price: {
                ...vehicle.price,
                downPayment: 0,
                monthlyPayment: 0
              }
            };
          })
        }
      }),
      { shouldSetLoaded: false }
    );
  }, [loadableInventoryState.setData]);

  const value = useMemo<ContextType>(
    () => ({
      inventory: loadableInventoryState,
      inventoryBulkData,
      previousSearchRequestId,
      resetInventoryPricing,
      setPreviousSearchRequestId,
      setSimilarVehicles,
      shouldCalculateEvWidthInventory,
      shouldCalculateEvWidthSimilar,
      similarVehicles,
      updateInventoryPricing
    }),
    [
      inventoryBulkData,
      loadableInventoryState,
      previousSearchRequestId,
      resetInventoryPricing,
      shouldCalculateEvWidthInventory,
      shouldCalculateEvWidthSimilar,
      similarVehicles,
      updateInventoryPricing
    ]
  );

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

InventoryContextProvider.displayName = 'InventoryContextProvider';

export default InventoryContext;

export const generateInventoryContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState,
    { isMergeableObject: isPlainObject }
  );

export function useInventoryIsLoaded(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.isLoaded;
}

export function useInventoryVehicles(): Vehicle[] {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.vehicles ?? [];
}

export function useIsInventoryLoaded(): boolean {
  return useContext(InventoryContext).inventory.isLoaded;
}

export function usePagination(): Partial<Pagination> {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination ?? {};
}

export function useSearchRequestId(): string {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.searchRequestId ?? '';
}

export function useSimilarVehicles() {
  const { similarVehicles } = useContext(InventoryContext);

  return similarVehicles;
}

export function useUserDeliveryInfo(): UserDeliveryInfo | undefined {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.userDeliveryInfo;
}

export function useRequestIds(): {
  searchRequestId: string;
  previousSearchRequestId: string;
} {
  const { inventory, previousSearchRequestId } = useContext(InventoryContext);

  return useMemo(
    () => ({
      searchRequestId: inventory.data.searchRequestId,
      previousSearchRequestId
    }),
    [inventory.data.searchRequestId, previousSearchRequestId]
  );
}

export function useInventoryBulkData(): Record<number, AvailabilityData> {
  const { inventoryBulkData } = useContext(InventoryContext);

  return inventoryBulkData.data ?? {};
}
export function useTotalMatchedInventory(): number {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination?.totalMatchedInventory ?? 0;
}
